import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Bookmark, Share2, Lock, Trash2, Plus, Globe, Loader2, Check, Star } from 'lucide-react';

/**
 * Viewer-facing Saved Filter Views: capture the current slicer selections
 * (global + page + per-visual) as a named view. Private by default; the owner
 * can share it with everyone in the tenant who can read the report. Each viewer
 * can also mark one view as their personal default, which auto-applies on open.
 */
export function SavedFilterViews({ reportId, getSelections, onApply }) {
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [appliedId, setAppliedId] = useState(null);
  const [defaultId, setDefaultId] = useState(null);
  const autoAppliedRef = useRef(false);
  const onApplyRef = useRef(onApply);
  useEffect(() => { onApplyRef.current = onApply; });

  const load = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      const res = await api.listFilterViews(reportId);
      setViews(res.views || []);
      setDefaultId(res.my_default_view_id || null);
      // Auto-apply the viewer's default view once, on first load.
      if (!autoAppliedRef.current && res.my_default_view_id) {
        const dv = (res.views || []).find(v => v.view_id === res.my_default_view_id);
        if (dv) { autoAppliedRef.current = true; onApplyRef.current(dv.selections || {}); setAppliedId(dv.view_id); }
      }
    } catch (e) { /* non-blocking */ }
    finally { setLoading(false); }
  }, [reportId]);

  // Load once on mount so a default view can auto-apply even before the
  // viewer opens the popover.
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const nm = name.trim();
    if (!nm) { toast.error('Name your view first'); return; }
    setSaving(true);
    try {
      const doc = await api.createFilterView(reportId, { name: nm, shared, selections: getSelections() });
      setViews(prev => [doc, ...prev]);
      setName(''); setShared(false);
      toast.success(shared ? 'View saved & shared' : 'View saved');
    } catch (e) { toast.error(e.message || 'Could not save view'); }
    finally { setSaving(false); }
  };

  const apply = (v) => {
    onApply(v.selections || {});
    setAppliedId(v.view_id);
    setOpen(false);
    toast.success(`Applied "${v.name}"`);
  };

  const toggleShare = async (v) => {
    try {
      await api.updateFilterView(reportId, v.view_id, { shared: !v.shared });
      setViews(prev => prev.map(x => x.view_id === v.view_id ? { ...x, shared: !x.shared } : x));
    } catch (e) { toast.error(e.message || 'Could not update'); }
  };

  const remove = async (v) => {
    try {
      await api.deleteFilterView(reportId, v.view_id);
      setViews(prev => prev.filter(x => x.view_id !== v.view_id));
      if (defaultId === v.view_id) setDefaultId(null);
      toast.success('View deleted');
    } catch (e) { toast.error(e.message || 'Could not delete'); }
  };

  const toggleDefault = async (v) => {
    const next = defaultId === v.view_id ? '' : v.view_id;
    setDefaultId(next || null);
    try {
      await api.setDefaultFilterView(reportId, next);
      toast.success(next ? `"${v.name}" is now your default` : 'Default cleared');
    } catch (e) { toast.error(e.message || 'Could not set default'); setDefaultId(defaultId); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-1 text-[9px] rounded-lg border border-border/40 bg-muted/20 px-2 py-1 text-foreground hover:border-primary/40 transition-colors flex-shrink-0"
          title="Saved filter views"
          data-testid="saved-views-trigger"
        >
          <Bookmark className="w-3 h-3 text-primary" /> Views{views.length ? ` (${views.length})` : ''}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2.5 z-[120]" data-testid="saved-views-popover">
        <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold text-foreground">
          <Bookmark className="w-3.5 h-3.5 text-primary" /> Saved filter views
        </div>

        <div className="space-y-1 max-h-56 overflow-y-auto mb-2.5">
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
          ) : views.length === 0 ? (
            <p className="text-[10px] text-muted-foreground px-1 py-2">No saved views yet. Capture the current filters below.</p>
          ) : views.map(v => (
            <div key={v.view_id} className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 border transition-colors ${appliedId === v.view_id ? 'border-primary/50 bg-primary/10' : 'border-border/30 hover:bg-muted/20'}`} data-testid={`saved-view-${v.view_id}`}>
              <button onClick={() => apply(v)} className="flex-1 min-w-0 text-left flex items-center gap-1.5" data-testid={`saved-view-apply-${v.view_id}`}>
                {appliedId === v.view_id ? <Check className="w-3 h-3 text-primary flex-shrink-0" /> : <Bookmark className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                <span className="text-[11px] text-foreground truncate">{v.name}</span>
                {v.shared && <Globe className="w-2.5 h-2.5 text-emerald-500 flex-shrink-0" title="Shared with your team" data-testid={`saved-view-shared-${v.view_id}`} />}
                {!v.is_owner && <span className="text-[8px] text-muted-foreground/70 truncate">· {v.created_by_name || 'shared'}</span>}
              </button>
              <button
                onClick={() => toggleDefault(v)}
                className={`flex-shrink-0 ${defaultId === v.view_id ? 'text-amber-400' : 'text-muted-foreground/60 hover:text-amber-400'}`}
                title={defaultId === v.view_id ? 'Your default — click to clear' : 'Set as your default view'}
                data-testid={`saved-view-default-${v.view_id}`}
              >
                <Star className="w-3 h-3" fill={defaultId === v.view_id ? 'currentColor' : 'none'} />
              </button>
              {v.is_owner && (
                <>
                  <button onClick={() => toggleShare(v)} className={`flex-shrink-0 ${v.shared ? 'text-emerald-500' : 'text-muted-foreground/60 hover:text-foreground'}`} title={v.shared ? 'Make private' : 'Share with team'} data-testid={`saved-view-share-${v.view_id}`}>
                    {v.shared ? <Globe className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
                  </button>
                  <button onClick={() => remove(v)} className="flex-shrink-0 text-muted-foreground/60 hover:text-destructive" title="Delete view" data-testid={`saved-view-delete-${v.view_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-border/40 pt-2 space-y-1.5">
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !saving) { e.preventDefault(); save(); } }}
            placeholder="Name this view…"
            className="h-7 text-[11px]"
            data-testid="saved-view-name-input"
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer" data-testid="saved-view-share-toggle">
              <input type="checkbox" checked={shared} onChange={e => setShared(e.target.checked)} className="accent-primary w-3 h-3" />
              {shared ? <Globe className="w-3 h-3 text-emerald-500" /> : <Lock className="w-3 h-3" />}
              {shared ? 'Shared with team' : 'Private to me'}
            </label>
            <Button size="sm" className="h-7 text-[11px] gap-1" disabled={saving || !name.trim()} onClick={save} data-testid="saved-view-save-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
